import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment-timezone';
import useWorkflow, {
    SavedWorkflow,
    WorkflowSummary,
    WorkflowVersionSummary,
} from '@/src/Activations/components/WorkflowBuilder/useWorkflow';
import {
    WorkflowCatalog,
    WorkflowDefinition,
    WorkflowNode,
    WorkflowEdge,
    TestRunResult,
    WorkflowRunSummary,
    CLICKERBOOTH_ACTIONS,
} from '@/src/Activations/components/WorkflowBuilder/types';
import { Switch } from '@/src/shadComponents/ui/switch';

/*
 * Workflows v2 — the automation builder as a spine, not a canvas
 * (per the workflow-builder mock): the workflow reads top-to-bottom,
 * branches render as indented lanes under condition chips, layout is
 * deterministic. Non-tree graphs (diamond merges) are detected at
 * load and bridge to the classic canvas — the data model is shared,
 * nothing is lost. Draft/publish split preserved.
 */

let nodeSeq = 0;
const newId = () => `v2n-${++nodeSeq}-${Math.random().toString(36).slice(2, 7)}`;

const STEP_LABELS: Record<string, string> = {
    trigger: 'Trigger',
    transform: 'Transform',
    httpRequest: 'HTTP request',
    lookupMedia: 'Look up media',
    branch: 'Branch',
    emitCodes: 'Device commands',
};

function nodeSummary(n: WorkflowNode): string {
    const d: any = n.data ?? {};
    switch (n.type) {
        case 'trigger':
            return d.eventName ?? d.triggerType ?? '';
        case 'transform':
            return (d.assignments ?? [])
                .map((a: any) => a.key)
                .join(', ');
        case 'httpRequest':
            return `${d.method ?? 'GET'} ${d.url ?? ''}`;
        case 'lookupMedia':
            return d.shareIdentifiers ?? '';
        case 'branch':
            return `${(d.conditions ?? []).length + 1} lanes`;
        case 'emitCodes':
            return (d.codes ?? []).map((c: any) => c.action).join(', ');
        default:
            return n.type;
    }
}

function defaultData(type: string): any {
    switch (type) {
        case 'transform':
            return { assignments: [{ key: '', expression: '' }] };
        case 'httpRequest':
            return { method: 'GET', url: '', headers: [], bodyMode: 'none' };
        case 'lookupMedia':
            return { shareIdentifiers: '' };
        case 'branch':
            return {
                conditions: [{ id: newId(), label: '', expression: '' }],
            };
        case 'emitCodes':
            return { codes: [{ action: 'navigate', fields: [] }] };
        default:
            return {};
    }
}

export default function WorkflowsV2() {
    const navigate = useNavigate();
    const { activationId } = useParams();
    const id = activationId ? +activationId : 0;
    const {
        listWorkflows,
        getWorkflow,
        saveWorkflow,
        createWorkflow,
        deleteWorkflow,
        publishWorkflow,
        testRun,
        getCatalog,
        listVersions,
        restoreVersion,
        listRuns,
    } = useWorkflow();

    const [workflows, setWorkflows] = useState<WorkflowSummary[]>([]);
    const [catalog, setCatalog] = useState<WorkflowCatalog | null>(null);
    /* null = the legacy singular qrScan workflow */
    const [selectedWf, setSelectedWf] = useState<number | null>(null);
    const [wf, setWf] = useState<SavedWorkflow | null>(null);
    const [snapshot, setSnapshot] = useState('');
    const [selectedNode, setSelectedNode] = useState<string | null>(null);
    const [addAfter, setAddAfter] = useState<{
        parent: string | null;
        handle?: string;
    } | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [pubBusy, setPubBusy] = useState(false);
    const [testInput, setTestInput] = useState('');
    const [testResult, setTestResult] = useState<TestRunResult | null>(null);
    const [testBusy, setTestBusy] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createFields, setCreateFields] = useState({
        name: '',
        triggerType: 'event',
        eventName: '',
    });
    const [versions, setVersions] = useState<WorkflowVersionSummary[]>([]);
    const [runs, setRuns] = useState<WorkflowRunSummary[]>([]);
    const [panel, setPanel] = useState<'versions' | 'runs' | null>(null);

    const loadWorkflow = async (workflowId: number | null) => {
        setSelectedWf(workflowId);
        setSelectedNode(null);
        setTestResult(null);
        setPanel(null);
        const w = await getWorkflow(id, workflowId).catch(() => null);
        if (w) {
            setWf(w);
            setSnapshot(JSON.stringify(w.definition));
        }
    };

    useEffect(() => {
        let mounted = true;
        (async () => {
            const [list, cat] = await Promise.all([
                listWorkflows(id).catch(() => []),
                getCatalog().catch(() => null),
            ]);
            if (!mounted) return;
            setWorkflows(list ?? []);
            setCatalog(cat);
            const first = (list ?? []).find(
                (w) => w.triggerType === 'qrScan',
            );
            await loadWorkflow(first ? first.id : null);
            setIsLoading(false);
        })();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const def: WorkflowDefinition = useMemo(
        () => wf?.definition ?? { nodes: [], edges: [] },
        [wf],
    );
    const dirty = wf != null && JSON.stringify(def) !== snapshot;

    /* ---- graph analysis: tree or bail to classic ---- */
    const graph = useMemo(() => {
        const incoming = new Map<string, number>();
        const bySource = new Map<string, WorkflowEdge[]>();
        def.nodes.forEach((n) => incoming.set(n.id, 0));
        def.edges.forEach((e) => {
            incoming.set(e.target, (incoming.get(e.target) ?? 0) + 1);
            (bySource.get(e.source) ?? bySource.set(e.source, []).get(e.source)!).push(e);
        });
        const roots = def.nodes.filter(
            (n) => (incoming.get(n.id) ?? 0) === 0,
        );
        const merged = def.nodes.some((n) => (incoming.get(n.id) ?? 0) > 1);
        /* cycle check via DFS from roots */
        let cyclic = false;
        const seen = new Set<string>();
        const walk = (nid: string, path: Set<string>) => {
            if (path.has(nid)) {
                cyclic = true;
                return;
            }
            if (seen.has(nid)) return;
            seen.add(nid);
            path.add(nid);
            (bySource.get(nid) ?? []).forEach((e) => walk(e.target, path));
            path.delete(nid);
        };
        roots.forEach((r) => walk(r.id, new Set()));
        const trigger =
            def.nodes.find((n) => n.type === 'trigger') ?? roots[0] ?? null;
        return {
            bySource,
            trigger,
            isTree: !merged && !cyclic && roots.length <= 1,
        };
    }, [def]);

    const nodeById = useMemo(
        () => new Map(def.nodes.map((n) => [n.id, n])),
        [def],
    );

    /* ---- mutations ---- */
    const setDef = (next: WorkflowDefinition) =>
        setWf((w) => (w ? { ...w, definition: next } : w));

    const updateNode = (nid: string, patch: Partial<WorkflowNode>) =>
        setDef({
            ...def,
            nodes: def.nodes.map((n) =>
                n.id === nid ? { ...n, ...patch } : n,
            ),
        });

    const updateData = (nid: string, dataPatch: any) => {
        const n = nodeById.get(nid);
        if (!n) return;
        updateNode(nid, { data: { ...(n.data as any), ...dataPatch } });
    };

    const subtreeIds = (nid: string): string[] => {
        const out = [nid];
        (graph.bySource.get(nid) ?? []).forEach((e) =>
            out.push(...subtreeIds(e.target)),
        );
        return out;
    };

    const removeNode = (nid: string) => {
        const doomed = new Set(subtreeIds(nid));
        if (
            doomed.size > 1 &&
            !window.confirm(
                `Remove this step and the ${doomed.size - 1} step${
                    doomed.size === 2 ? '' : 's'
                } after it?`,
            )
        )
            return;
        setDef({
            ...def,
            nodes: def.nodes.filter((n) => !doomed.has(n.id)),
            edges: def.edges.filter(
                (e) => !doomed.has(e.source) && !doomed.has(e.target),
            ),
        });
        setSelectedNode(null);
    };

    const addStep = (type: string) => {
        const target = addAfter;
        setAddAfter(null);
        const node: WorkflowNode = {
            id: newId(),
            type,
            label: STEP_LABELS[type] ?? type,
            data: defaultData(type),
        };
        const edges = [...def.edges];
        if (target?.parent) {
            edges.push({
                id: newId(),
                source: target.parent,
                target: node.id,
                ...(target.handle ? { sourceHandle: target.handle } : {}),
            });
        }
        setDef({ ...def, nodes: [...def.nodes, node], edges });
        setSelectedNode(node.id);
    };

    const ensureTrigger = () => {
        const node: WorkflowNode = {
            id: newId(),
            type: 'trigger',
            label: 'Trigger',
            data: {
                triggerType:
                    workflows.find((w) => w.id === selectedWf)?.triggerType ??
                    catalog?.defaultTriggerType ??
                    'qrScan',
            },
        };
        setDef({ ...def, nodes: [...def.nodes, node] });
        setSelectedNode(node.id);
    };

    /* ---- persistence ---- */
    const save = async () => {
        if (!wf || busy) return;
        setBusy(true);
        const saved = await saveWorkflow(
            id,
            def,
            wf.enabled,
            selectedWf,
        ).catch(() => null);
        if (saved) {
            setWf({ ...wf, hasUnpublishedChanges: true });
            setSnapshot(JSON.stringify(def));
        }
        setBusy(false);
    };

    const publish = async () => {
        if (!wf || pubBusy) return;
        setPubBusy(true);
        if (dirty) await saveWorkflow(id, def, wf.enabled, selectedWf).catch(() => null);
        const r = await publishWorkflow(id, undefined, selectedWf).catch(
            () => null,
        );
        if (r) {
            setWf({
                ...wf,
                publishedVersionNumber: r.publishedVersionNumber,
                hasUnpublishedChanges: false,
            });
            setSnapshot(JSON.stringify(def));
        }
        setPubBusy(false);
    };

    const runTest = async () => {
        if (testBusy) return;
        setTestBusy(true);
        setTestResult(await testRun(id, def, testInput));
        setTestBusy(false);
    };

    const openPanel = async (which: 'versions' | 'runs') => {
        setPanel((p) => (p === which ? null : which));
        if (which === 'versions')
            setVersions(
                (await listVersions(id, selectedWf).catch(() => null))
                    ?.versions ?? [],
            );
        else
            setRuns(
                (await listRuns(id, { limit: 10 }).catch(() => null))?.runs ??
                    [],
            );
    };

    /* ---- rendering ---- */
    const laneChain = (startId: string | null): React.ReactNode => {
        const items: React.ReactNode[] = [];
        let current = startId;
        while (current) {
            const n = nodeById.get(current);
            if (!n) break;
            const children = graph.bySource.get(n.id) ?? [];
            items.push(renderNode(n));
            if (n.type === 'branch') {
                items.push(renderBranchLanes(n, children));
                current = null;
            } else {
                current = children[0]?.target ?? null;
                if (children.length === 0)
                    items.push(addRow(n.id, undefined));
            }
        }
        return items;
    };

    const renderNode = (n: WorkflowNode) => (
        <div
            key={n.id}
            className={
                'relative pb-[22px] pl-[34px] ' +
                (n.type === 'branch' ? '' : '')
            }
        >
            {(graph.bySource.get(n.id) ?? []).length > 0 &&
                n.type !== 'branch' && (
                    <span className="absolute bottom-0 left-[10px] top-[30px] w-px bg-line" />
                )}
            <span
                className={
                    'absolute left-0 top-1 flex h-[21px] w-[21px] items-center justify-center rounded-full text-[10px] ' +
                    (n.type === 'trigger'
                        ? 'bg-good-soft text-good'
                        : 'bg-secondary text-muted-foreground')
                }
            >
                {n.type === 'trigger'
                    ? '⚡'
                    : n.type === 'branch'
                    ? '⑂'
                    : n.type === 'httpRequest'
                    ? '⊕'
                    : n.type === 'emitCodes'
                    ? '▸'
                    : '≡'}
            </span>
            <button
                type="button"
                onClick={() => setSelectedNode(n.id)}
                className={
                    'inline-flex max-w-full cursor-pointer items-baseline gap-2 rounded-[11px] border bg-transparent px-[13px] py-[9px] text-left font-[inherit] ' +
                    (selectedNode === n.id
                        ? 'border-brand shadow-[0_0_0_1px_hsl(var(--brand))]'
                        : 'border-border hover:border-faint')
                }
            >
                <span className="whitespace-nowrap text-[13px] font-semibold tracking-[-0.01em]">
                    {n.label || STEP_LABELS[n.type] || n.type}
                </span>
                <span className="max-w-[300px] overflow-hidden text-ellipsis whitespace-nowrap font-mono text-[10.5px] text-faint">
                    {nodeSummary(n)}
                </span>
            </button>
        </div>
    );

    const renderBranchLanes = (
        branch: WorkflowNode,
        children: WorkflowEdge[],
    ) => {
        const conditions = ((branch.data as any)?.conditions ?? []) as any[];
        const laneFor = (handle: string | undefined) =>
            children.find((e) => (e.sourceHandle ?? 'else') === (handle ?? 'else'))
                ?.target ?? null;
        return (
            <div key={`${branch.id}-lanes`} className="ml-[22px]">
                {[...conditions, null].map((c: any, i: number) => (
                    <div
                        key={c?.id ?? 'else'}
                        className="relative ml-0 mt-2 border-l-[1.5px] border-border pb-1 pl-4 pt-2.5"
                    >
                        <span
                            className={
                                'mb-2.5 inline-block rounded-full px-2.5 py-[3px] font-mono text-[9.5px] uppercase tracking-[0.08em] ' +
                                (c
                                    ? 'bg-brand-soft text-brand-ink'
                                    : 'bg-secondary text-muted-foreground')
                            }
                        >
                            {c
                                ? c.label || c.expression || `condition ${i + 1}`
                                : 'otherwise'}
                        </span>
                        <div>{laneChain(laneFor(c?.id))}</div>
                        {laneFor(c?.id) == null &&
                            addRow(branch.id, c?.id ?? 'else')}
                    </div>
                ))}
            </div>
        );
    };

    const addRow = (parent: string | null, handle: string | undefined) => (
        <div key={`add-${parent}-${handle ?? ''}`} className="relative pl-[34px] pt-0.5">
            <div className="relative">
                <button
                    type="button"
                    onClick={() =>
                        setAddAfter(
                            addAfter?.parent === parent &&
                                addAfter?.handle === handle
                                ? null
                                : { parent, handle },
                        )
                    }
                    className="flex cursor-pointer items-center gap-2 border-0 bg-transparent p-0 font-[inherit] text-[12.5px] text-faint hover:text-foreground"
                >
                    <span className="absolute -left-[34px] flex h-[21px] w-[21px] items-center justify-center rounded-full border-[1.5px] border-dashed border-off bg-background text-[12px]">
                        +
                    </span>
                    Add a step
                </button>
                {addAfter?.parent === parent && addAfter?.handle === handle && (
                    <div className="absolute left-0 top-7 z-40 w-[220px] rounded-[13px] border border-border bg-background p-1.5 shadow-lg">
                        {(catalog?.steps ?? [
                            { type: 'transform', label: 'Transform' },
                            { type: 'httpRequest', label: 'HTTP request' },
                            { type: 'lookupMedia', label: 'Look up media' },
                            { type: 'branch', label: 'Branch' },
                            { type: 'emitCodes', label: 'Device commands' },
                        ]).map((s2: any) => (
                            <button
                                key={s2.type}
                                type="button"
                                onClick={() => addStep(s2.type)}
                                className="block w-full cursor-pointer rounded-[8px] border-0 bg-transparent px-2.5 py-1.5 text-left font-[inherit] text-[12.5px] font-semibold hover:bg-secondary"
                            >
                                {s2.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    /* ---- rail editors ---- */
    const kvRows = (
        rows: any[],
        onChange: (rows: any[]) => void,
        aKey: string,
        bKey: string,
        aPh: string,
        bPh: string,
    ) => (
        <>
            {rows.map((r, i) => (
                <div key={i} className="mt-2 flex items-center gap-2">
                    <input
                        className="ps-input w-[38%] font-mono text-[11px]"
                        placeholder={aPh}
                        value={r[aKey] ?? ''}
                        onChange={(e) =>
                            onChange(
                                rows.map((x, xi) =>
                                    xi === i
                                        ? { ...x, [aKey]: e.target.value }
                                        : x,
                                ),
                            )
                        }
                    />
                    <input
                        className="ps-input flex-1 font-mono text-[11px]"
                        placeholder={bPh}
                        value={r[bKey] ?? ''}
                        onChange={(e) =>
                            onChange(
                                rows.map((x, xi) =>
                                    xi === i
                                        ? { ...x, [bKey]: e.target.value }
                                        : x,
                                ),
                            )
                        }
                    />
                    <button
                        type="button"
                        onClick={() =>
                            onChange(rows.filter((_, xi) => xi !== i))
                        }
                        className="cursor-pointer border-0 bg-transparent p-0 text-[12px] text-faint hover:text-attention"
                    >
                        ×
                    </button>
                </div>
            ))}
        </>
    );

    const railEditor = () => {
        const n = selectedNode ? nodeById.get(selectedNode) : null;
        if (!n)
            return (
                <p className="text-[12.5px] text-muted-foreground">
                    Select a step to configure it.
                </p>
            );
        const d: any = n.data ?? {};
        return (
            <div>
                <div className="mb-2.5 flex items-center justify-between">
                    <span className="ps-label">
                        {STEP_LABELS[n.type] ?? n.type}
                    </span>
                    {n.type !== 'trigger' && (
                        <button
                            type="button"
                            onClick={() => removeNode(n.id)}
                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11.5px] font-semibold text-faint hover:text-attention"
                        >
                            Remove
                        </button>
                    )}
                </div>
                <div className="mb-3 grid grid-cols-2 gap-x-4">
                    <div>
                        <label className="mb-1 block text-[11.5px] font-semibold text-muted-foreground">
                            Label
                        </label>
                        <input
                            className="ps-input w-full text-[12.5px]"
                            value={n.label ?? ''}
                            onChange={(e) =>
                                updateNode(n.id, { label: e.target.value })
                            }
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-[11.5px] font-semibold text-muted-foreground">
                            Step key
                        </label>
                        <input
                            className="ps-input w-full font-mono text-[11.5px]"
                            placeholder="variable name"
                            value={n.key ?? ''}
                            onChange={(e) =>
                                updateNode(n.id, { key: e.target.value })
                            }
                        />
                    </div>
                </div>

                {n.type === 'trigger' && (
                    <p className="text-[12px] text-muted-foreground">
                        Fires{' '}
                        {d.eventName
                            ? `on device event "${d.eventName}"`
                            : `on ${d.triggerType ?? 'scan'}`}
                        . Input is available as{' '}
                        <span className="font-mono text-[11px]">
                            scannedInput
                        </span>
                        .
                    </p>
                )}

                {n.type === 'transform' && (
                    <>
                        <label className="mb-1 block text-[11.5px] font-semibold text-muted-foreground">
                            Assignments
                        </label>
                        {kvRows(
                            d.assignments ?? [],
                            (rows) => updateData(n.id, { assignments: rows }),
                            'key',
                            'expression',
                            'name',
                            'expression',
                        )}
                        <button
                            type="button"
                            onClick={() =>
                                updateData(n.id, {
                                    assignments: [
                                        ...(d.assignments ?? []),
                                        { key: '', expression: '' },
                                    ],
                                })
                            }
                            className="mt-2 cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11.5px] font-semibold text-brand-ink"
                        >
                            + Add assignment
                        </button>
                    </>
                )}

                {n.type === 'httpRequest' && (
                    <>
                        <div className="mb-2 inline-flex gap-0.5 rounded-[9px] bg-secondary p-[3px]">
                            {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map(
                                (m) => (
                                    <button
                                        key={m}
                                        type="button"
                                        onClick={() =>
                                            updateData(n.id, { method: m })
                                        }
                                        className={
                                            'cursor-pointer rounded-[7px] border-0 px-2 py-1 font-[inherit] text-[10.5px] font-semibold ' +
                                            ((d.method ?? 'GET') === m
                                                ? 'bg-background text-foreground'
                                                : 'bg-transparent text-muted-foreground')
                                        }
                                    >
                                        {m}
                                    </button>
                                ),
                            )}
                        </div>
                        <input
                            className="ps-input w-full font-mono text-[11.5px]"
                            placeholder="https://…"
                            value={d.url ?? ''}
                            onChange={(e) =>
                                updateData(n.id, { url: e.target.value })
                            }
                        />
                        <label className="mb-1 mt-3 block text-[11.5px] font-semibold text-muted-foreground">
                            Headers
                        </label>
                        {kvRows(
                            d.headers ?? [],
                            (rows) => updateData(n.id, { headers: rows }),
                            'key',
                            'value',
                            'Header',
                            'Value',
                        )}
                        <button
                            type="button"
                            onClick={() =>
                                updateData(n.id, {
                                    headers: [
                                        ...(d.headers ?? []),
                                        { key: '', value: '' },
                                    ],
                                })
                            }
                            className="mt-2 cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11.5px] font-semibold text-brand-ink"
                        >
                            + Add header
                        </button>
                        <label className="mb-1 mt-3 block text-[11.5px] font-semibold text-muted-foreground">
                            Body
                        </label>
                        <div className="inline-flex gap-0.5 rounded-[9px] bg-secondary p-[3px]">
                            {['none', 'json', 'raw', 'multipart'].map((m) => (
                                <button
                                    key={m}
                                    type="button"
                                    onClick={() =>
                                        updateData(n.id, { bodyMode: m })
                                    }
                                    className={
                                        'cursor-pointer rounded-[7px] border-0 px-2.5 py-1 font-[inherit] text-[10.5px] font-semibold capitalize ' +
                                        ((d.bodyMode ?? 'none') === m
                                            ? 'bg-background text-foreground'
                                            : 'bg-transparent text-muted-foreground')
                                    }
                                >
                                    {m}
                                </button>
                            ))}
                        </div>
                        {['json', 'raw'].includes(d.bodyMode) && (
                            <textarea
                                className="mt-2 h-[80px] w-full resize-none rounded-[10px] border border-border bg-sunk p-2.5 font-mono text-[11px] text-foreground outline-none focus:border-brand"
                                value={d.body ?? ''}
                                onChange={(e) =>
                                    updateData(n.id, { body: e.target.value })
                                }
                            />
                        )}
                        {d.bodyMode === 'multipart' && (
                            <p className="mt-1.5 text-[11px] text-faint">
                                Multipart parts keep their saved config —
                                edit in classic for now.
                            </p>
                        )}
                    </>
                )}

                {n.type === 'lookupMedia' && (
                    <>
                        <label className="mb-1 block text-[11.5px] font-semibold text-muted-foreground">
                            Share identifiers expression
                        </label>
                        <input
                            className="ps-input w-full font-mono text-[11.5px]"
                            value={d.shareIdentifiers ?? ''}
                            onChange={(e) =>
                                updateData(n.id, {
                                    shareIdentifiers: e.target.value,
                                })
                            }
                        />
                    </>
                )}

                {n.type === 'branch' && (
                    <>
                        <label className="mb-1 block text-[11.5px] font-semibold text-muted-foreground">
                            Conditions{' '}
                            <span className="font-normal text-faint">
                                — first match wins; otherwise runs last
                            </span>
                        </label>
                        {kvRows(
                            d.conditions ?? [],
                            (rows) => updateData(n.id, { conditions: rows }),
                            'label',
                            'expression',
                            'Label',
                            'expression',
                        )}
                        <button
                            type="button"
                            onClick={() =>
                                updateData(n.id, {
                                    conditions: [
                                        ...(d.conditions ?? []),
                                        {
                                            id: newId(),
                                            label: '',
                                            expression: '',
                                        },
                                    ],
                                })
                            }
                            className="mt-2 cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11.5px] font-semibold text-brand-ink"
                        >
                            + Add condition
                        </button>
                    </>
                )}

                {n.type === 'emitCodes' && (
                    <>
                        {(d.codes ?? []).map((c: any, ci: number) => (
                            <div
                                key={ci}
                                className="mt-2 rounded-[10px] border border-border p-2.5"
                            >
                                <div className="flex items-center gap-2">
                                    <select
                                        className="ps-input flex-1 cursor-pointer appearance-none font-mono text-[11.5px]"
                                        value={c.action}
                                        onChange={(e) =>
                                            updateData(n.id, {
                                                codes: d.codes.map(
                                                    (x: any, xi: number) =>
                                                        xi === ci
                                                            ? {
                                                                  ...x,
                                                                  action: e
                                                                      .target
                                                                      .value,
                                                              }
                                                            : x,
                                                ),
                                            })
                                        }
                                    >
                                        {CLICKERBOOTH_ACTIONS.map((a) => (
                                            <option key={a} value={a}>
                                                {a}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            updateData(n.id, {
                                                codes: d.codes.filter(
                                                    (_: any, xi: number) =>
                                                        xi !== ci,
                                                ),
                                            })
                                        }
                                        className="cursor-pointer border-0 bg-transparent p-0 text-[12px] text-faint hover:text-attention"
                                    >
                                        ×
                                    </button>
                                </div>
                                {kvRows(
                                    c.fields ?? [],
                                    (rows) =>
                                        updateData(n.id, {
                                            codes: d.codes.map(
                                                (x: any, xi: number) =>
                                                    xi === ci
                                                        ? {
                                                              ...x,
                                                              fields: rows,
                                                          }
                                                        : x,
                                            ),
                                        }),
                                    'key',
                                    'valueExpression',
                                    'field',
                                    'expression',
                                )}
                                <button
                                    type="button"
                                    onClick={() =>
                                        updateData(n.id, {
                                            codes: d.codes.map(
                                                (x: any, xi: number) =>
                                                    xi === ci
                                                        ? {
                                                              ...x,
                                                              fields: [
                                                                  ...(x.fields ??
                                                                      []),
                                                                  {
                                                                      key: '',
                                                                      valueExpression:
                                                                          '',
                                                                  },
                                                              ],
                                                          }
                                                        : x,
                                            ),
                                        })
                                    }
                                    className="mt-1.5 cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11px] font-semibold text-brand-ink"
                                >
                                    + Add field
                                </button>
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={() =>
                                updateData(n.id, {
                                    codes: [
                                        ...(d.codes ?? []),
                                        { action: 'navigate', fields: [] },
                                    ],
                                })
                            }
                            className="mt-2 cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11.5px] font-semibold text-brand-ink"
                        >
                            + Add command
                        </button>
                    </>
                )}

                {!['trigger', 'transform', 'httpRequest', 'lookupMedia', 'branch', 'emitCodes'].includes(
                    n.type,
                ) && (
                    <textarea
                        className="h-[120px] w-full resize-none rounded-[10px] border border-border bg-sunk p-2.5 font-mono text-[11px] text-foreground outline-none focus:border-brand"
                        value={JSON.stringify(n.data, null, 2)}
                        onChange={(e) => {
                            try {
                                updateNode(n.id, {
                                    data: JSON.parse(e.target.value),
                                });
                            } catch {
                                /* keep typing */
                            }
                        }}
                    />
                )}
            </div>
        );
    };

    if (isLoading) {
        return (
            <main className="mx-auto max-w-[1060px] px-[clamp(18px,3vw,34px)] pt-4">
                {[0, 1, 2].map((i) => (
                    <div
                        key={i}
                        className="mb-4 h-12 animate-pulse rounded-[12px] bg-secondary"
                    />
                ))}
            </main>
        );
    }

    const currentSummary = workflows.find((w) => w.id === selectedWf);

    return (
        <main className="mx-auto max-w-[1060px] px-[clamp(18px,3vw,34px)] pt-4">
            <div className="ps-label mb-3.5">Workflows</div>

            {/* selector + state */}
            <div className="mb-[18px] flex flex-wrap items-center gap-1.5">
                {workflows.map((w) => (
                    <button
                        key={w.id}
                        type="button"
                        onClick={() => loadWorkflow(w.id)}
                        className={
                            'inline-flex cursor-pointer items-center gap-1.5 rounded-full px-3 py-[6px] font-[inherit] text-[12px] font-semibold ' +
                            (selectedWf === w.id
                                ? 'border border-transparent bg-brand-soft text-brand-ink'
                                : 'border border-border bg-transparent text-muted-foreground')
                        }
                    >
                        <span className="font-mono text-[8.5px] uppercase tracking-[0.07em] opacity-70">
                            {w.triggerType === 'qrScan'
                                ? 'QR'
                                : w.triggerType}
                        </span>
                        {w.name ?? `workflow ${w.id}`}
                    </button>
                ))}
                {creating ? (
                    <span className="inline-flex items-center gap-2">
                        <input
                            autoFocus
                            className="ps-input w-[140px] text-[12.5px]"
                            placeholder="Name"
                            value={createFields.name}
                            onChange={(e) =>
                                setCreateFields((f) => ({
                                    ...f,
                                    name: e.target.value,
                                }))
                            }
                        />
                        <select
                            className="ps-input cursor-pointer appearance-none text-[12px]"
                            value={createFields.triggerType}
                            onChange={(e) =>
                                setCreateFields((f) => ({
                                    ...f,
                                    triggerType: e.target.value,
                                }))
                            }
                        >
                            {(catalog?.triggers ?? [
                                { type: 'event', label: 'Event' },
                                { type: 'share', label: 'Share' },
                                { type: 'mediaUploaded', label: 'Media' },
                            ]).map((t) => (
                                <option key={t.type} value={t.type}>
                                    {t.label}
                                </option>
                            ))}
                        </select>
                        {createFields.triggerType === 'event' && (
                            <input
                                className="ps-input w-[120px] font-mono text-[11.5px]"
                                placeholder="event name"
                                value={createFields.eventName}
                                onChange={(e) =>
                                    setCreateFields((f) => ({
                                        ...f,
                                        eventName: e.target.value,
                                    }))
                                }
                            />
                        )}
                        <button
                            type="button"
                            disabled={!createFields.name.trim()}
                            onClick={async () => {
                                const created = await createWorkflow(id, {
                                    name: createFields.name.trim(),
                                    triggerType:
                                        createFields.triggerType as any,
                                    eventName:
                                        createFields.eventName.trim() ||
                                        undefined,
                                }).catch(() => null);
                                setCreating(false);
                                setCreateFields({
                                    name: '',
                                    triggerType: 'event',
                                    eventName: '',
                                });
                                if (created) {
                                    setWorkflows(
                                        await listWorkflows(id).catch(
                                            () => workflows,
                                        ),
                                    );
                                    loadWorkflow(created.id);
                                }
                            }}
                            className="cursor-pointer rounded-full border-0 bg-brand px-3.5 py-1.5 font-[inherit] text-[12px] font-semibold text-white disabled:opacity-50"
                        >
                            Create
                        </button>
                    </span>
                ) : (
                    <button
                        type="button"
                        onClick={() => setCreating(true)}
                        className="cursor-pointer rounded-full border border-dashed border-border bg-transparent px-3 py-[6px] font-[inherit] text-[12px] font-semibold text-faint hover:text-foreground"
                    >
                        + New workflow
                    </button>
                )}

                <span className="ml-auto flex items-center gap-3 text-[11.5px] text-faint">
                    {wf?.publishedVersionNumber != null && (
                        <span>v{wf.publishedVersionNumber} published</span>
                    )}
                    {(dirty || wf?.hasUnpublishedChanges) && (
                        <span className="font-semibold text-attention">
                            unpublished changes
                        </span>
                    )}
                    <label className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
                        Enabled
                        <Switch
                            checked={!!wf?.enabled}
                            onCheckedChange={(v) =>
                                setWf((w) =>
                                    w ? { ...w, enabled: v } : w,
                                )
                            }
                        />
                    </label>
                    {dirty && (
                        <button
                            type="button"
                            disabled={busy}
                            onClick={save}
                            className="cursor-pointer rounded-full border-0 bg-brand px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-white"
                        >
                            {busy ? 'Saving…' : 'Save'}
                        </button>
                    )}
                    <button
                        type="button"
                        disabled={pubBusy}
                        onClick={publish}
                        className="cursor-pointer rounded-full border-0 bg-foreground px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-background"
                    >
                        {pubBusy ? 'Publishing…' : 'Publish'}
                    </button>
                </span>
            </div>

            {!graph.isTree ? (
                <div className="max-w-[52ch] rounded-[12px] border border-border p-4 text-[13px] text-muted-foreground">
                    This workflow's graph has merging or cyclic paths, which
                    the v2 spine can't display.{' '}
                    <button
                        type="button"
                        onClick={() =>
                            navigate(`/activations/${id}/workflow`)
                        }
                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] font-semibold text-brand-ink"
                    >
                        Open it in the classic canvas →
                    </button>
                </div>
            ) : (
                <div className="grid gap-0 md:grid-cols-[1fr_320px]">
                    {/* MAIN spine */}
                    <div className="md:pr-10">
                        {graph.trigger == null ? (
                            <div className="pb-2">
                                <p className="mb-3 max-w-[46ch] text-[13px] text-muted-foreground">
                                    An empty workflow. Start with the trigger
                                    — everything else hangs off it.
                                </p>
                                <button
                                    type="button"
                                    onClick={ensureTrigger}
                                    className="cursor-pointer rounded-full border-0 bg-foreground px-[15px] py-2 font-[inherit] text-[13px] font-semibold text-background"
                                >
                                    Add the trigger
                                </button>
                            </div>
                        ) : (
                            laneChain(graph.trigger.id)
                        )}
                    </div>

                    {/* RAIL */}
                    <div className="mt-9 md:mt-0 md:border-l md:border-line md:pl-10">
                        <div className="mb-[26px]">{railEditor()}</div>

                        <div className="border-t border-line pt-4">
                            <div className="ps-label mb-2">Test run</div>
                            <textarea
                                className="h-[54px] w-full resize-none rounded-[10px] border border-border bg-sunk p-2.5 font-mono text-[11px] text-foreground outline-none focus:border-brand"
                                placeholder="Scanned input…"
                                value={testInput}
                                onChange={(e) => setTestInput(e.target.value)}
                            />
                            <button
                                type="button"
                                disabled={testBusy}
                                onClick={runTest}
                                className="mt-2 cursor-pointer rounded-full border border-border bg-transparent px-3.5 py-1.5 font-[inherit] text-[12px] font-semibold text-foreground hover:border-faint"
                            >
                                {testBusy ? 'Running…' : 'Run with this input'}
                            </button>
                            {testResult && (
                                <div className="mt-2.5 font-mono text-[10px] leading-relaxed">
                                    {testResult.success ? (
                                        <div className="font-semibold text-good">
                                            ✓ completed
                                        </div>
                                    ) : (
                                        <div className="font-semibold text-attention">
                                            ✗ {testResult.error}
                                        </div>
                                    )}
                                    {(testResult.trace ?? []).map(
                                        (t: any, i: number) => (
                                            <div
                                                key={i}
                                                className="text-faint"
                                            >
                                                {i + 1}{' '}
                                                {t.nodeLabel ??
                                                    t.nodeType ??
                                                    t.nodeId}{' '}
                                                {t.status ?? ''}
                                            </div>
                                        ),
                                    )}
                                    {(testResult.output ?? []).map(
                                        (o: any, i: number) => (
                                            <div
                                                key={`o${i}`}
                                                className="text-muted-foreground"
                                            >
                                                → {o.action}{' '}
                                                {JSON.stringify(o.data)}
                                            </div>
                                        ),
                                    )}
                                </div>
                            )}
                            <div className="mt-3.5 flex gap-3.5 text-[12px] font-semibold">
                                <button
                                    type="button"
                                    onClick={() => openPanel('runs')}
                                    className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] font-semibold text-brand-ink"
                                >
                                    Run history {panel === 'runs' ? '▴' : '→'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => openPanel('versions')}
                                    className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] font-semibold text-brand-ink"
                                >
                                    Versions{' '}
                                    {panel === 'versions' ? '▴' : '→'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        const bundle = {
                                            name:
                                                currentSummary?.name ??
                                                'qr-scan',
                                            triggerType:
                                                currentSummary?.triggerType ??
                                                'qrScan',
                                            eventName:
                                                currentSummary?.eventName ??
                                                undefined,
                                            enabled: !!wf?.enabled,
                                            definition: def,
                                        };
                                        const blob = new Blob(
                                            [
                                                JSON.stringify(
                                                    bundle,
                                                    null,
                                                    2,
                                                ),
                                            ],
                                            { type: 'application/json' },
                                        );
                                        const url =
                                            URL.createObjectURL(blob);
                                        const a =
                                            document.createElement('a');
                                        a.href = url;
                                        a.download = `workflow-${
                                            bundle.name ?? 'export'
                                        }.json`;
                                        a.click();
                                        URL.revokeObjectURL(url);
                                    }}
                                    className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] font-semibold text-brand-ink"
                                >
                                    Export
                                </button>
                                <label className="cursor-pointer font-semibold text-brand-ink">
                                    Import
                                    <input
                                        type="file"
                                        accept="application/json,.json"
                                        className="hidden"
                                        onChange={async (e) => {
                                            const f = e.target.files?.[0];
                                            e.target.value = '';
                                            if (!f) return;
                                            try {
                                                const parsed = JSON.parse(
                                                    await f.text(),
                                                );
                                                const definition =
                                                    parsed?.definition
                                                        ?.nodes != null
                                                        ? parsed.definition
                                                        : parsed?.nodes !=
                                                          null
                                                        ? parsed
                                                        : null;
                                                if (
                                                    definition &&
                                                    window.confirm(
                                                        'Replace this workflow\'s steps with the imported file? Nothing is saved until you hit Save.',
                                                    )
                                                )
                                                    setDef(definition);
                                            } catch {
                                                /* ignore bad files */
                                            }
                                        }}
                                    />
                                </label>
                                {currentSummary && (
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            if (
                                                !window.confirm(
                                                    `Delete workflow "${currentSummary.name}"?`,
                                                )
                                            )
                                                return;
                                            await deleteWorkflow(
                                                id,
                                                currentSummary.id,
                                            ).catch(() => null);
                                            const list =
                                                await listWorkflows(id).catch(
                                                    () => [],
                                                );
                                            setWorkflows(list);
                                            loadWorkflow(
                                                list[0]?.id ?? null,
                                            );
                                        }}
                                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] font-semibold text-faint hover:text-attention"
                                    >
                                        Delete
                                    </button>
                                )}
                            </div>
                            {panel === 'runs' && (
                                <div className="mt-2.5">
                                    {runs.length === 0 && (
                                        <p className="text-[11.5px] text-faint">
                                            No runs yet.
                                        </p>
                                    )}
                                    {runs.map((r) => (
                                        <div
                                            key={r.id}
                                            className="flex items-baseline gap-2 border-t border-line py-1.5 text-[11.5px] first:border-t-0"
                                        >
                                            <span
                                                className={
                                                    'font-semibold ' +
                                                    (r.status === 'completed'
                                                        ? 'text-good'
                                                        : r.status ===
                                                          'failed'
                                                        ? 'text-attention'
                                                        : 'text-faint')
                                                }
                                            >
                                                {r.status}
                                            </span>
                                            <span className="min-w-0 flex-1 truncate font-mono text-[10px] text-muted-foreground">
                                                {r.inputPreview ?? ''}
                                            </span>
                                            <span className="whitespace-nowrap text-faint">
                                                {moment(
                                                    r.createdAt,
                                                ).fromNow(true)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {panel === 'versions' && (
                                <div className="mt-2.5">
                                    {versions.length === 0 && (
                                        <p className="text-[11.5px] text-faint">
                                            Nothing published yet.
                                        </p>
                                    )}
                                    {versions.map((v) => (
                                        <div
                                            key={v.id}
                                            className="flex items-baseline gap-2.5 border-t border-line py-1.5 text-[11.5px] first:border-t-0"
                                        >
                                            <span className="font-semibold tabular-nums">
                                                v{v.versionNumber}
                                            </span>
                                            <span className="min-w-0 flex-1 truncate text-muted-foreground">
                                                {v.notes ?? ''}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    const restored =
                                                        await restoreVersion(
                                                            id,
                                                            v.id,
                                                            selectedWf,
                                                        ).catch(() => null);
                                                    if (restored)
                                                        setDef(restored);
                                                }}
                                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11px] font-semibold text-brand-ink"
                                            >
                                                Restore
                                            </button>
                                            <span className="whitespace-nowrap text-faint">
                                                {moment(
                                                    v.createdAt,
                                                ).fromNow(true)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}
